import PublicIcon from '@material-ui/icons/Public';

import { useRef, useState } from "react";
import { Tooltip, Box, IconButton, Popover, Select, MenuItem, InputAdornment, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useBoolean } from "../../hooks/useBoolean";
import { splitDMS } from "../../utils/FPEUtils";

const useStyles = makeStyles(() => ({
  coordinateBtn: {
    position: "absolute",
    right: 3,
  },
  coordinateBox: {
    padding: 20,
    display: 'flex',
    flexWrap: 'wrap',
  },
  coordinateText: {
    width: 65,
    paddingTop: 3,
    paddingRight: 10,
  },
  coordinateMark: {
    width: 35,
  },
}));

const validateValue = (value, min, max, includeMax = false) => {
  if (value !== "") {
    const _value = parseFloat(value);
    if (_value < min) {
      return false;
    }
    if (includeMax) {
      return _value <= max;
    } else {
      return _value < max;
    }
  } else {
    return false;
  }
};

const validateDeg = (type, deg, min) => {
  const max = type === 'lon' ? 180 : 90;
  if (!validateValue(deg, 0, max, true)) {
    return false;
  }
  if (parseInt(deg) === max) {
    return min === "" || parseFloat(min) === 0;
  } else {
    return true;
  }
};

const validateMin = (type, deg, min) => {
  const _min = min === "" ? 0 : min;
  if (!validateValue(_min, 0, 60)) {
    return false;
  }
  const max = type === 'lon' ? 180 : 90;
  if (deg !== "" && parseInt(deg) === max) {
    return parseFloat(_min) === 0;
  } else {
    return true;
  }
};

//緯度経度用Popover
const CoordinatePopover = (props) => {
  const {type, value, handleClose, prevValue, ...other } = props;

  const markRef = useRef(null);
  const degRef = useRef(null);
  const minRef = useRef(null);

  const handleOnClose = (event) => {
    const mark = markRef.current.value;
    let deg = degRef.current.value;
    let min = minRef.current.value !== "" ? minRef.current.value : 0;
    let newValue = "";
    if (validateDeg(type, deg, min) && validateMin(type, deg, min)) {
      deg = parseInt(deg);
      min = parseFloat(min);
      newValue = deg + min / 60.0;
      if (mark === 1) {
        newValue *= -1;
      }
    }
    handleClose(newValue);
  };

  const handleEnter = () => {
    setTimeout(()=> {
      handleOnClose();
    }, 1);
  };

  return (
    <Popover onClose={handleOnClose} {...other}>
      <CoordinateBox type={type} value={value} prevValue={prevValue} 
        markRef={markRef} degRef={degRef} minRef={minRef} handleEnter={handleEnter}/>
    </Popover>
  );
};

//Popover内の緯度経度入力BOX
const CoordinateBox = ({type, value, prevValue, markRef, degRef, minRef, handleEnter}) => {

  let _dm;
  if (value === null) {
    let _mark;
    if (prevValue === undefined || prevValue === null || prevValue === "") {
      _mark = 1;//E or N
    } else {
      _mark = Math.sign(prevValue);
    }
    _dm = [_mark, "", ""];
  } else {
    _dm = splitDMS(value);
    const seconds = _dm.pop();
    _dm[2] = _dm[2] + parseFloat(seconds) / 60.0;
  }

  const [dm, setDM] = useState(_dm);
  const [degValidateError, setDegError, setDegValid] = useBoolean(!validateDeg(type, dm[1], dm[2]));
  const [minValidateError, setMinError, setMinValid] = useBoolean(!validateMin(type, dm[1], dm[2]));

  const handleMarkChange = (event) => {
    setDM([event.target.value === 0 ? 1 : -1, dm[1], dm[2]]);
  };

  const handleDegChange = (event) => {
    let deg = event.target.value;
    if (deg !== "") {
      deg = parseFloat(deg);
      //負数の場合、正数に変更
      if (deg < 0) {
        deg = Math.abs(deg);
      }
      //小数は切り捨て
      if (!Number.isInteger(deg)) {
        deg = Math.floor(deg);
      }
      degRef.current.value = deg;

      //範囲チェック
      if (validateDeg(type, deg, minRef.current.value)) {
        setDegValid();
      } else {
        setDegError();
      }
    } else {
      setDegError();
    }
    setDM([dm[0], deg, dm[2]]);
  };

  const handleMinChange = (event) => {
    let min = event.target.value;
    if (min !== "") {
      min = parseFloat(min);
      //負数の場合、正数に変更
      if (min < 0) {
        min = Math.abs(min);
      }
      minRef.current.value = min;

      //範囲チェック
      if (validateMin(type, degRef.current.value, min)) {
        setMinValid();
      } else {
        setMinError();
      }
    } else {
      setMinError();
    }
    setDM([dm[0], dm[1], min]);
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      handleEnter();
    }
  }

  const marks = type === 'lon' ? ['E', 'W'] : ['N', 'S'];
  const markIdx = dm[0] >= 0 ? 0 : 1;
  const maxDegree = type === 'lon' ? 180 : 90;

  const classes = useStyles();

  return (
    <Box className={classes.coordinateBox}>
      <TextField
        id="coordinate-degrees"
        type="number"
        size="small"
        className={classes.coordinateText}
        defaultValue={dm[1]}
        onChange={handleDegChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: <InputAdornment position="end">°</InputAdornment>,
          inputProps: { min: 0, max: maxDegree },
        }}
        inputRef={degRef}
        error={degValidateError}
        autoFocus
      />
      <TextField
        id="coordinate-minutes"
        type="number"
        size="small"
        className={classes.coordinateText}
        defaultValue={dm[2]}
        onChange={handleMinChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: <InputAdornment position="end">′</InputAdornment>,
          inputProps: { min: 0, max: 60 },
        }}
        inputRef={minRef}
        error={minValidateError}
      />
      <Select
        id="coordinate-mark"
        className={classes.coordinateMark}
        value={markIdx}
        inputRef={markRef}
        onChange={handleMarkChange}
      >
        {marks.map((mark, index) => {
          return <MenuItem key={index} value={index}>{mark}</MenuItem>
        })}
      </Select>
    </Box>
  );
};

//緯度経度ボタン
const CoordinateButton = ({ type, value, disabled, handleInputEnd, prevValue, style }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (newValue) => {
    setAnchorEl(null);
    handleInputEnd(value, newValue);
  };

  const open = !!anchorEl;
  const classes = useStyles();

  return (
    <>
      <IconButton aria-label={`input ${type}`} size="small" className={classes.coordinateBtn}
        disabled={disabled} onClick={handleClick}
        style={style}>
        <Tooltip title={`input ${type}`} arrow >
          <PublicIcon fontSize="small" color={disabled ? "disabled" : "action"} />
        </Tooltip>
      </IconButton>
      <CoordinatePopover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        type={type}
        value={value}
        prevValue={prevValue}
        handleClose={handleClose}
      />
    </>
  );
};

export default CoordinateButton;
